<template>
  <div>
    <NuxtLayout name="default">
      <!-- profile section -->
      <section class="py-space-lg pb-lg-space-xxxl">
        <div class="container px-lg-0">
          <div class="row">
            <div class="col-12 col-lg-9">
              <!-- title -->
              <h1 class="h1-medium pb-space-sm">
                {{ $page?.admission.title }}
              </h1>
              <!-- text -->
              <div
                class="fs-medium pb-space-md"
                v-html="$page?.admission.description"
              ></div>
            </div>
          </div>
          <!-- cards -->
          <div class="row card-deck row-cols-1 row-cols-md-2 row-cols-lg-3">
            <div
              class="col"
              v-for="(profile, index) in $page?.admission.personas"
              :key="index"
            >
              <CardImageTile
                :bg-image-src="profile?.image!"
                :image-alt="profile?.title"
                :clickable-card="true"
                :title="profile?.title!"
                :text="profile?.text!"
                :cta-text="profile?.htmlLinks?.[0]?.text!"
                :cta-link="profile?.htmlLinks?.[0]?.uri!"
                :cta-link-target="
                formatLinkTarget(profile?.htmlLinks?.[0]?.target as string)
              "
                text-size="large"
                :display-cta-button="true"
                cta-text-variant="dark-3"
                @cardClick="handleCardClick(profile, $event)"
                @ctaClick="handleCardClick(profile, $event)"
                @cardEnter="
                  handleEnterKeyEvent(profile?.htmlLinks?.[0]?.uri as string)
                "
                class="mb-space-sm"
              />
            </div>
          </div>
        </div>
      </section>
      <!-- section-rfi -->
      <SectionRfiMultiStep />
    </NuxtLayout>
  </div>
</template>

<script lang="ts" setup>
import { analyticsComposable } from "@rds-vue-ui/analytics-gs-composable";
import { CardImageTile } from "@rds-vue-ui/card-image-tile";
import SectionRfiMultiStep from "~/components/rfi/SectionRfiMultiStep.vue";
import {
  Admission,
  ParagraphCardInformation,
} from "~/server/resolvers/resolvers-types";
const config = useRuntimeConfig();
const siteUrl = config.public.siteUrl;
/*
 ********************************************
 * Page query
 ********************************************
 */
interface AdmissionPageQuery {
  admission: Admission;
}
definePageMeta({
  layout: false,
  __dangerouslyDisableSanitizersByTagID: {
    "ldjson-schema": ["innerHTML"],
  },
});
const $page = await usePageQuery<AdmissionPageQuery>(
  `#graphql
    query Admission($admissionId: ID!) {
        admission(id: $admissionId) {
          id
          detailPage
          metaTitle
          metaDescription
          title
          description
          personas {
            id
            title
            text
            image
            htmlLinks {
              text
              uri
              target
            }
          }
        }
      }
    `,
  {
    admissionId: "bf1a7eb5-3d64-461b-8196-1f3fd59ea6b8",
  },
  {
    key: "admission-home",
  }
);

/*
 *******************************************
 * Page meta information
 ********************************************
 */
const jsonLdMarkup = useSchemaService().generateAdmissionPageSchema(
  $page?.admission
);
useHead({
  title: $page?.admission.metaTitle,
  meta: [
    {
      name: "title",
      content: $page?.admission.metaTitle,
    },
    {
      name: "description",
      content: $page?.admission.metaTitle,
    },
  ],
  link: [
    {
      rel: "canonical",
      href: useUtilityService().formatCanonicalUrl(
        `${siteUrl}${$page?.admission.detailPage}`
      ),
    },
  ],
  script: [jsonLdMarkup],
});

/*
 ********************************************
 * Methods
 ********************************************
 */
const formatLinkTarget = (target: string) => {
  return useUtilityService().formatLinkTarget(target);
};
type Profile = ParagraphCardInformation | null;
const handleCardClick = (profile: Profile, event: Event) => {
  event.preventDefault();
  analyticsComposable.trackLinkEvent(
    "internal link",
    "main content",
    profile?.title?.toLowerCase() || "N/A",
    profile?.htmlLinks?.[0]?.text?.toLowerCase() || "N/A",
    "hover card"
  );
  const linkHref = profile?.htmlLinks?.[0]?.uri as string;
  const linkTarget = formatLinkTarget(
    profile?.htmlLinks?.[0]?.target as string
  );
  navigateTo(linkHref, {
    open: {
      target: linkTarget,
    },
  });
};
const handleEnterKeyEvent = (link: string) => {
  navigateTo(link, {
    open: {
      target: "_self",
    },
  });
};
</script>
